import React, { FC, MouseEventHandler } from 'react';

import {
  Container,
  ContainerProps,
  Tabs,
  TabsProvider,
} from '@lglp/components';
import { MenuItem, SearchMenu, MainMenuItem } from '@lglp/prismic-client';
import { isVoid } from '@lglp/utils';

import { HeaderLogo } from '../HeaderLogo';
import { MenuLocales } from '../MenuLocales';
import { MenuRechercher } from '../MenuRechercher';
import { CloseMenu } from './CloseMenu';
import DesktopContext, { useInitializeContext } from './DesktopContext';
import styles from './DesktopLayout.module.scss';
import { MenuContent } from './MenuContent';

export const DesktopLayout: FC<DesktopLayoutProps> = ({
  menuItems,
  searchMenu,
  className,
  ...rest
}) => {
  const providerValue = useInitializeContext();

  const onOpenMenu =
    (menuItem: MenuItem): MouseEventHandler =>
    (event) => {
      if (providerValue.isMenuOpen(0, menuItem)) {
        providerValue.close();
      } else {
        providerValue.openMenu(0, menuItem);
        event.preventDefault();
      }
    };

  return (
    <DesktopContext.Provider value={providerValue}>
      <Container className={[styles.desktopMenu, className]} {...rest}>
        <HeaderLogo />

        <Container>
          <Container as="nav">
            <TabsProvider uid="desktop-header-menu-tabs">
              <Tabs.List as="ul" className={styles.menu}>
                {menuItems?.map((menuItem, i) => (
                  <Container
                    key={i}
                    as="li"
                    className={
                      providerValue.isMenuOpen(1, menuItem) && styles.selected
                    }
                    onClick={onOpenMenu(menuItem)}
                  >
                    <Tabs.Button index={i}>
                      <span>{menuItem.label}</span>
                    </Tabs.Button>
                  </Container>
                ))}
              </Tabs.List>

              <Container className={styles.openedMenu}>
                {!isVoid(providerValue.openedMenus) && (
                  <>
                    <CloseMenu />
                    {menuItems.map((menuItem, i) => (
                      <Tabs.Panel
                        index={i}
                        key={i}
                        className={styles.menuContent}
                      >
                        <MenuContent menuItem={menuItem} />
                      </Tabs.Panel>
                    ))}
                  </>
                )}
              </Container>
            </TabsProvider>
          </Container>
        </Container>

        <Container className={styles.extraMenu}>
          {searchMenu && (
            <MenuRechercher search={searchMenu} className={styles.searchMenu} />
          )}
          <MenuLocales className={styles.localsMenu} />
        </Container>
      </Container>
    </DesktopContext.Provider>
  );
};

type DesktopLayoutProps = ContainerProps & {
  menuItems: MainMenuItem[];
  searchMenu: SearchMenu;
};
